/**
 * Admin Billing & Subscriptions panel (Iter 62).
 *
 * Three sections:
 *  A) Pending switch requests — vendor-initiated; admin approves (optionally setting a new rate) or denies.
 *  B) Monthly-plan subscriptions snapshot — per-vendor lifecycle status, with "Mark paid" / "Mark failed"
 *     manual controls (essential for sandbox + reconciling out-of-band payments) and a
 *     one-click "Run suspension check" button.
 *  C) Platform grace-days setting — how many past_due days before listings auto-suspend.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet, CheckCircle2, XCircle, AlertTriangle, Info, RefreshCcw, Calendar } from "lucide-react";
import AdminPayseraConfigCard from "@/components/admin/AdminPayseraConfigCard";

function _statusPill(status) {
  const base = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border";
  if (status === "active") return <span className={`${base} bg-emerald-50 border-emerald-200 text-emerald-700`} data-testid="admin-sub-status-active"><CheckCircle2 className="h-3 w-3" />active</span>;
  if (status === "past_due") return <span className={`${base} bg-amber-50 border-amber-200 text-amber-800`} data-testid="admin-sub-status-past-due"><AlertTriangle className="h-3 w-3" />past due</span>;
  if (status === "suspended") return <span className={`${base} bg-rose-50 border-rose-200 text-rose-700`} data-testid="admin-sub-status-suspended"><XCircle className="h-3 w-3" />suspended</span>;
  return <span className={`${base} bg-blue-50 border-blue-200 text-blue-700`} data-testid="admin-sub-status-manual"><Info className="h-3 w-3" />manual</span>;
}

function ApproveDialog({ request, onClose, onSaved }) {
  const [value, setValue] = useState(request?.current_value || (request?.requested_mode === "monthly_per_car" ? 30 : 12));
  const [reduced, setReduced] = useState(0);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!request) return null;
  const isMonthly = request.requested_mode === "monthly_per_car";
  const submit = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/admin/billing/switch-requests/${request.id}/decide`, {
        decision,
        value: decision === "approved" ? Number(value) : null,
        reduced_percent: decision === "approved" && isMonthly ? Number(reduced) : null,
        admin_note: note.trim() || null,
      });
      toast.success(decision === "approved" ? "Switch approved" : "Switch denied");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="admin-switch-decide-dialog">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5">
        <h3 className="font-display text-lg mb-2">Decide switch request</h3>
        <p className="text-xs text-neutral-600 mb-3">
          <strong>{request.vendor_name || request.vendor_email}</strong> wants to switch to{" "}
          <strong>{isMonthly ? "monthly per vehicle" : "commission per booking"}</strong>.
        </p>
        {request.customer_note && (
          <p className="text-xs text-neutral-500 italic bg-neutral-50 border border-border rounded-lg px-2.5 py-1.5 mb-3">"{request.customer_note}"</p>
        )}
        <div className="space-y-2.5 mb-3">
          <label className="block">
            <span className="text-[11px] font-semibold text-neutral-600">{isMonthly ? "EUR per vehicle / month" : "Commission %"}</span>
            <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1" data-testid="admin-switch-decide-value" />
          </label>
          {isMonthly && (
            <label className="block">
              <span className="text-[11px] font-semibold text-neutral-600">Reduced per-booking commission % (0 = none)</span>
              <Input type="number" value={reduced} onChange={(e) => setReduced(e.target.value)} className="mt-1" data-testid="admin-switch-decide-reduced" />
            </label>
          )}
          <label className="block">
            <span className="text-[11px] font-semibold text-neutral-600">Admin note (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="w-full mt-1 rounded-lg border border-border px-2.5 py-1.5 text-sm" rows={2} maxLength={500} data-testid="admin-switch-decide-note" />
          </label>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Button onClick={() => submit("denied")} disabled={busy} variant="outline" size="sm" className="rounded-full" data-testid="admin-switch-deny">Deny</Button>
          <Button onClick={() => submit("approved")} disabled={busy} size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="admin-switch-approve">Approve</Button>
          <Button onClick={onClose} disabled={busy} variant="ghost" size="sm" className="rounded-full" data-testid="admin-switch-cancel">Cancel</Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBillingPanel() {
  const [pending, setPending] = useState([]);
  const [subs, setSubs] = useState({ vendors: [], grace_days: 7, stripe_available: false });
  const [decideTarget, setDecideTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [graceInput, setGraceInput] = useState(7);

  const load = async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        api.get("/admin/billing/switch-requests?status=pending"),
        api.get("/admin/billing/subscriptions"),
      ]);
      setPending(pRes.data || []);
      setSubs(sRes.data || { vendors: [], grace_days: 7, stripe_available: false });
      setGraceInput(sRes.data?.grace_days || 7);
    } catch (e) {
      console.error("billing panel load failed:", e);
    }
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (vendorId) => {
    setBusy(true);
    try { await api.post(`/admin/billing/subscriptions/${vendorId}/mark-paid`, {}); toast.success("Marked paid"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const markFailed = async (vendorId) => {
    setBusy(true);
    try { await api.post(`/admin/billing/subscriptions/${vendorId}/mark-failed`, {}); toast.success("Marked failed"); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const runSuspensionCheck = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/billing/run-suspension-check", {});
      toast.success(`Scanned ${data.scanned}, suspended ${data.suspended}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const saveGraceDays = async () => {
    const d = Math.max(1, Math.min(90, Number(graceInput) || 7));
    setBusy(true);
    try { await api.patch("/admin/billing/grace-days", { days: d }); toast.success(`Grace period set to ${d} days`); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-white shadow-sm" data-testid="admin-billing-panel">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2 flex-wrap">
        <Wallet className="h-4 w-4 text-blue-600" />
        <h2 className="font-display text-lg">Billing & subscriptions</h2>
        {!subs.stripe_available && (
          <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full font-semibold" data-testid="admin-billing-sandbox-flag">
            sandbox — manual billing only
          </span>
        )}
      </div>

      {/* A) Pending switch requests */}
      <section className="px-5 py-4 border-b border-border" data-testid="admin-billing-switch-requests">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="font-semibold text-sm">Pending switch requests <span className="text-xs text-neutral-500">({pending.length})</span></h3>
        </div>
        {pending.length === 0 ? (
          <p className="text-xs text-neutral-500">No vendor switch requests right now.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {pending.map((r) => (
              <li key={r.id} className="px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-switch-request-${r.id}`}>
                <div className="text-sm">
                  <div className="font-semibold">{r.vendor_name || r.vendor_email}</div>
                  <div className="text-xs text-neutral-500">
                    {r.current_mode} → <strong>{r.requested_mode}</strong>
                    {r.customer_note && <span className="italic"> · "{r.customer_note}"</span>}
                  </div>
                </div>
                <Button onClick={() => setDecideTarget(r)} size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid={`admin-switch-decide-${r.id}`}>Decide</Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* B) Monthly-plan subscriptions */}
      <section className="px-5 py-4 border-b border-border" data-testid="admin-billing-subscriptions">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="font-semibold text-sm">Monthly-plan vendors <span className="text-xs text-neutral-500">({subs.vendors.length})</span></h3>
          <Button onClick={runSuspensionCheck} disabled={busy} variant="outline" size="sm" className="rounded-full" data-testid="admin-billing-run-suspension">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />Run suspension check
          </Button>
        </div>
        {subs.vendors.length === 0 ? (
          <p className="text-xs text-neutral-500">No vendors are on the monthly plan.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-xs">
              <thead className="bg-neutral-50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Vendor</th>
                  <th className="px-3 py-2 font-medium">Rate</th>
                  <th className="px-3 py-2 font-medium">Active cars</th>
                  <th className="px-3 py-2 font-medium">Monthly due</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {subs.vendors.map((v) => (
                  <tr key={v.vendor_id} className="border-t border-border" data-testid={`admin-billing-row-${v.vendor_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{v.business_name || v.email}</div>
                      <div className="text-[10px] text-neutral-500">{v.email}</div>
                    </td>
                    <td className="px-3 py-2">€{Number(v.commission_value || 0).toFixed(2)}/car{v.reduced_percent > 0 ? ` + ${v.reduced_percent}%` : ""}</td>
                    <td className="px-3 py-2">{v.active_car_count}</td>
                    <td className="px-3 py-2">€{Number(v.monthly_fee_due || 0).toFixed(2)}</td>
                    <td className="px-3 py-2">{_statusPill(v.subscription_status)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button onClick={() => markPaid(v.vendor_id)} disabled={busy} size="sm" variant="ghost" className="rounded-full text-emerald-700 hover:text-emerald-800" data-testid={`admin-billing-mark-paid-${v.vendor_id}`}>Mark paid</Button>
                      <Button onClick={() => markFailed(v.vendor_id)} disabled={busy} size="sm" variant="ghost" className="rounded-full text-rose-700 hover:text-rose-800" data-testid={`admin-billing-mark-failed-${v.vendor_id}`}>Mark failed</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* C) Grace days */}
      <section className="px-5 py-4 border-b border-border" data-testid="admin-billing-grace-days">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><Calendar className="h-3.5 w-3.5" />Suspension grace period</h3>
        <p className="text-xs text-neutral-500 mb-2">Days a vendor can remain past-due before their listings are auto-hidden.</p>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={90}
            value={graceInput}
            onChange={(e) => setGraceInput(e.target.value)}
            className="w-24"
            data-testid="admin-billing-grace-input"
          />
          <span className="text-xs text-neutral-600">days</span>
          <Button onClick={saveGraceDays} disabled={busy} size="sm" className="rounded-full bg-blue-600 hover:bg-blue-700 ml-2" data-testid="admin-billing-grace-save">Save</Button>
        </div>
      </section>

      <ApproveDialog request={decideTarget} onClose={() => setDecideTarget(null)} onSaved={load} />

      {/* D) Paysera gateway */}
      <section className="px-5 py-5" data-testid="admin-billing-paysera">
        <AdminPayseraConfigCard />
      </section>
    </div>
  );
}
